import { TRUST_BOUNDARY } from "./constants";
import { dollars } from "./money";
import type { BuyerFundingReview, BuyerPortalSubmission, OpenGap, ReviewEvent, SourceRow } from "./types";

export function buyerPortalToken(review: BuyerFundingReview): string {
  const slug = (review.fileNumber || review.reviewNumber).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `buyer-${review.id}-${slug.slice(0, 12)}`;
}

export function buyerPortalPath(review: BuyerFundingReview): string {
  return `#/buyer/${encodeURIComponent(review.id)}/${encodeURIComponent(buyerPortalToken(review))}`;
}

export function buyerPortalUrl(review: BuyerFundingReview): string {
  if (typeof window === "undefined") return buyerPortalPath(review);
  return `${window.location.origin}${window.location.pathname}${buyerPortalPath(review)}`;
}

export function buildBuyerPortalSubmissionPacket(review: BuyerFundingReview) {
  return {
    accessLink: buyerPortalPath(review),
    fullUrl: typeof window !== "undefined" ? buyerPortalUrl(review) : buyerPortalPath(review),
    token: buyerPortalToken(review),
    status: "scoped_local_link",
    submittedCount: review.buyerPortalSubmissions?.length || 0,
    submissions: review.buyerPortalSubmissions || [],
    boundary: TRUST_BOUNDARY
  };
}

function nextId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function applyBuyerPortalSubmission(
  review: BuyerFundingReview,
  submission: BuyerPortalSubmission
): BuyerFundingReview {
  const nowIso = new Date().toISOString();
  const buyer = review.buyerParties[0];
  const portalSourceRows: SourceRow[] = [];

  const addSourceRow = (
    sourceType: SourceRow["sourceType"],
    title: string,
    limitation: string,
    extractedFields?: Record<string, string | number | boolean | null>
  ): string => {
    const row: SourceRow = {
      id: nextId("src"),
      reviewId: review.id,
      sourceType,
      title,
      provider: "Buyer portal",
      recordedBy: submission.buyerName || "Buyer portal",
      recordedAt: submission.submittedAt,
      sourceDocumentKey: submission.sourceFileName || submission.paymentFileName,
      sourceHash: submission.sourceFileHash || submission.paymentFileHash,
      extractedFields,
      limitation,
      claimIds: []
    };
    portalSourceRows.push(row);
    return row.id;
  };

  const ackSourceId = addSourceRow(
    "buyer_acknowledgment",
    "Buyer portal instruction acknowledgment",
    "Buyer portal acknowledgment recorded as evidence for office review.",
    {
      instructionVersion: submission.acknowledgedInstructionVersion ?? null,
      acknowledged: submission.acknowledgedInstructions
    }
  );

  const sourceEvidenceId = addSourceRow(
    "source_account_evidence",
    `Buyer portal ${submission.sourceEvidenceType.replaceAll("_", " ")}`,
    "Buyer-provided source evidence. No automated bank-account check was performed in this portal flow.",
    {
      sourcePartyName: submission.sourcePartyName ?? null,
      bankName: submission.sourceBankName ?? null,
      accountLast4: submission.sourceAccountLast4 ?? null
    }
  );

  const paymentEvidenceId = addSourceRow(
    "payment_initiation",
    `Buyer portal ${submission.paymentReceiptType.replaceAll("_", " ")}`,
    "Buyer-provided payment receipt or initiation evidence. The office must compare it to inbound receipt evidence.",
    {
      amountSent: submission.amountSent?.amountCents ?? null,
      traceReference: submission.traceReference ?? null
    }
  );

  const newGaps: OpenGap[] = [];
  if (submission.conflictReported) {
    newGaps.push({
      id: nextId("gap"),
      reviewId: review.id,
      gapType: "Instruction mismatch",
      severity: "stop_hold",
      description:
        submission.conflictDescription ||
        "Buyer reported conflicting funding instructions through the buyer portal.",
      sourceRowIds: [ackSourceId],
      officeActionRequired: true,
      exceptionRequired: true,
      reviewerNote: "Buyer portal conflict report",
      status: "retained"
    });
  }
  if (submission.sourceEvidenceType === "unable_to_provide") {
    newGaps.push({
      id: nextId("gap"),
      reviewId: review.id,
      gapType: "Missing source account evidence",
      severity: "requires_attention",
      description: "Buyer marked source evidence unable to provide in portal.",
      sourceRowIds: [sourceEvidenceId],
      officeActionRequired: true,
      exceptionRequired: false,
      reviewerNote: "Buyer portal unable to provide source evidence",
      status: "retained"
    });
  }
  if (submission.paymentReceiptType === "unable_to_provide") {
    newGaps.push({
      id: nextId("gap"),
      reviewId: review.id,
      gapType: "Missing initiation evidence",
      severity: "recorded",
      description: "Buyer marked payment receipt unable to provide in portal.",
      sourceRowIds: [paymentEvidenceId],
      officeActionRequired: false,
      exceptionRequired: false,
      reviewerNote: "Buyer portal unable to provide payment receipt",
      status: "retained"
    });
  }

  const portalEvent: ReviewEvent = {
    id: nextId("evt"),
    reviewId: review.id,
    actorId: submission.buyerEmail || submission.buyerName || "buyer",
    eventType: "Buyer portal submission received",
    occurredAt: nowIso,
    objectType: "BuyerPortalSubmission",
    objectId: submission.id,
    sourceRowIds: portalSourceRows.map((row) => row.id),
    after: {
      acknowledgedInstructions: submission.acknowledgedInstructions,
      sourceEvidenceType: submission.sourceEvidenceType,
      paymentReceiptType: submission.paymentReceiptType,
      conflictReported: submission.conflictReported
    }
  };

  const ownerMatch =
    submission.sourcePartyName &&
    buyer?.name &&
    submission.sourcePartyName.toLowerCase().includes(buyer.name.toLowerCase().split(" ")[0] ?? "");

  return {
    ...review,
    updatedAt: nowIso,
    buyerPortalSubmissions: [
      ...(review.buyerPortalSubmissions || []).filter((item) => item.id !== submission.id),
      { ...submission, status: "attached_to_review" }
    ],
    sourceRows: [...review.sourceRows, ...portalSourceRows],
    buyerAcknowledgment: submission.acknowledgedInstructions
      ? {
          ...review.buyerAcknowledgment,
          type: "Portal acknowledgment",
          partyName: submission.buyerName || buyer?.name,
          acknowledgedAmount: submission.acknowledgedAmount || review.expectedFunds.amount || review.expectedAmount,
          acknowledgedInstructionVersion:
            submission.acknowledgedInstructionVersion || review.fundingInstruction.versionLabel,
          acknowledgedRail: review.paymentRail.rail,
          acknowledgedAt: submission.submittedAt,
          channel: "Buyer portal",
          sourceDocumentId: ackSourceId,
          recordedBy: submission.buyerName || "Buyer portal",
          limitation:
            "Buyer acknowledgment recorded through the buyer portal as evidence for the office review."
        }
      : review.buyerAcknowledgment,
    sourceAccountEvidence:
      submission.sourceEvidenceType !== "unable_to_provide"
        ? {
            ...review.sourceAccountEvidence,
            evidenceType: submission.sourceEvidenceType.replaceAll("_", " "),
            provider: submission.sourceEvidenceType === "connect_bank_later" ? "plaid" : "manual",
            recordedAt: submission.submittedAt,
            bankName: submission.sourceBankName || review.sourceAccountEvidence.bankName,
            accountLast4: submission.sourceAccountLast4 || review.sourceAccountEvidence.accountLast4,
            ownerName: submission.sourcePartyName || submission.buyerName,
            ownerMatchResult: ownerMatch ? "Matched" : "Partially matched",
            sourceParty: submission.sourcePartyName || submission.buyerName,
            limitation:
              submission.sourceEvidenceType === "connect_bank_later"
                ? "Plaid connection requested by buyer; live Plaid remains externally blocked. Evidence recorded when available."
                : "Buyer-provided source evidence through portal."
          }
        : review.sourceAccountEvidence,
    paymentInitiation:
      submission.paymentReceiptType !== "not_initiated" &&
      submission.paymentReceiptType !== "unable_to_provide"
        ? {
            ...review.paymentInitiation,
            evidenceType: submission.paymentReceiptType.replaceAll("_", " "),
            rail: review.paymentRail.rail,
            initiatedAmount: submission.amountSent,
            initiatedAt: submission.dateSent,
            initiatedBy: submission.buyerName,
            senderShown: submission.sourcePartyName || submission.buyerName,
            traceNumber: submission.traceReference,
            reference: submission.traceReference,
            fileMemo: review.fileNumber,
            sourceDocumentId: paymentEvidenceId,
            limitation:
              "Buyer-provided initiation evidence recorded through portal; inbound receipt remains required before funds are relied on."
          }
        : review.paymentInitiation,
    fundingSource: submission.thirdPartyExplanation
      ? {
          ...review.fundingSource,
          limitation: submission.thirdPartyExplanation
        }
      : review.fundingSource,
    openGaps: [...review.openGaps, ...newGaps],
    events: [...review.events, portalEvent],
    fundsStatus:
      submission.acknowledgedInstructions && review.fundsStatus === "Not requested"
        ? "Buyer acknowledged"
        : review.fundsStatus
  };
}

export function createPortalSubmission(
  review: BuyerFundingReview,
  token: string,
  form: {
    buyerName: string;
    buyerEmail?: string;
    acknowledgedInstructions: boolean;
    sourceEvidenceType: BuyerPortalSubmission["sourceEvidenceType"];
    sourcePartyName?: string;
    sourceBankName?: string;
    sourceAccountLast4?: string;
    sourceFileName?: string;
    sourceFileHash?: string;
    paymentReceiptType: BuyerPortalSubmission["paymentReceiptType"];
    amountSent?: number;
    dateSent?: string;
    traceReference?: string;
    paymentFileName?: string;
    paymentFileHash?: string;
    thirdPartyExplanation?: string;
    conflictReported?: boolean;
    conflictDescription?: string;
    buyerNote?: string;
  }
): BuyerPortalSubmission {
  return {
    id: nextId("buyer-sub"),
    reviewId: review.id,
    token,
    submittedAt: new Date().toISOString(),
    buyerName: form.buyerName,
    buyerEmail: form.buyerEmail,
    acknowledgedInstructions: form.acknowledgedInstructions,
    acknowledgedInstructionVersion: review.fundingInstruction.versionLabel,
    acknowledgedAmount: review.expectedFunds.amount || review.expectedAmount,
    sourceEvidenceType: form.sourceEvidenceType,
    sourcePartyName: form.sourcePartyName,
    sourceBankName: form.sourceBankName,
    sourceAccountLast4: form.sourceAccountLast4?.replace(/\D/g, "").slice(-4),
    sourceFileName: form.sourceFileName,
    sourceFileHash: form.sourceFileHash,
    paymentReceiptType: form.paymentReceiptType,
    amountSent: form.amountSent !== undefined ? dollars(form.amountSent) : undefined,
    dateSent: form.dateSent,
    traceReference: form.traceReference,
    paymentFileName: form.paymentFileName,
    paymentFileHash: form.paymentFileHash,
    thirdPartyExplanation: form.thirdPartyExplanation,
    conflictReported: Boolean(form.conflictReported),
    conflictDescription: form.conflictDescription,
    buyerNote: form.buyerNote,
    status: "submitted"
  };
}
